import { useState } from "react";

import { useLocation } from "react-router";

import {
    ArrowRight,
    Markdown,
    useAnalytics,
    useAnalyticsAttributes
} from "../../components";
import { UiNavLinkModel } from "../../models";
import { renderViewModel } from "../../render-view-model";
import { getColorInitial } from "../../utils";

import {
    NavLinkAnchor,
    NavIcon,
    NavLinkTitleContainer,
    NavLinkTitle,
    NavLinkDescription
} from "./UiNavLink.styles";

export type UiNavLinkProps = {
    readonly model: UiNavLinkModel;
    readonly tabIndex?: number;
};

const UiNavLink = ({
    model: {
        id,
        content: {
            entryTitle,
            titleHeading,
            description,
            url,
            iconColor,
            addBackgroundColorOnHover,
            decorator,
            uiIcon
        }
    },
    tabIndex
}: UiNavLinkProps): JSX.Element => {
    const location = useLocation();
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();
    const [decoratorOnHover, setDecoratorOnHover] = useState(false);

    return (
        <NavLinkAnchor
            aria-current={location?.pathname === url ? "page" : undefined}
            href={url}
            onClick={() =>
                sendTrackEvent("clickedUiNavLink", {
                    eventContainer: "navigation",
                    ...analyticsAttributes,
                    event: "clicked",
                    eventComponent: "link",
                    label: titleHeading,
                    labelEnglish: entryTitle,
                    uuid: id,
                    href: url
                })
            }
            data-uuid={id}
            hoverColorInitial={
                addBackgroundColorOnHover || iconColor
                    ? getColorInitial(iconColor, true)
                    : undefined
            }
            data-testid="ui-nav-link"
            onMouseEnter={() => setDecoratorOnHover(true)}
            onMouseLeave={() => setDecoratorOnHover(false)}
            tabIndex={tabIndex}
        >
            <NavLinkTitleContainer>
                {uiIcon && (
                    <NavIcon iconColorInitial={iconColor?.charAt(0)}>
                        {renderViewModel(uiIcon)}
                    </NavIcon>
                )}
                <NavLinkTitle>{titleHeading}</NavLinkTitle>
                {decorator && decoratorOnHover && <ArrowRight color="P300" />}
            </NavLinkTitleContainer>
            <NavLinkDescription>
                <Markdown links={false}>{description}</Markdown>
            </NavLinkDescription>
        </NavLinkAnchor>
    );
};

export default UiNavLink;
